import { useMemo, useState } from 'react';
import {
  Box,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { useTranslation } from 'react-i18next';
import theme from '../../../theme/theme';
import { DetailCard } from '../../../components/ui/DetailCard';
import { BADGE_VARIANTS, type BadgeVariant } from '../../../components/ui/badgeVariants';
import { formatDate, dateFormatForLang } from '../../../lib/date/formatDate';
import { formatPayslipAmount, formatPayslipNumber } from '../config/payslipFormat';
import {
  RUBRIQUE_CATEGORY_LABEL_KEY,
  RUBRIQUE_CATEGORY_VARIANT,
} from '../config/payslipRubriquePresentation';
import {
  RUBRIQUE_CATEGORY_ORDER,
  isRubriqueInactive,
  orderedRubriques,
  resolveRubriqueCategory,
  rubriqueBase,
  type RubriqueCategory,
} from '../payslipRubriques';
import type { PayslipLine } from '../payslipTypes';

const I18N = 'payments:payslips.detail';

const categoryColors = (category: RubriqueCategory) =>
  BADGE_VARIANTS[RUBRIQUE_CATEGORY_VARIANT[category]];

/** A rounded legend marker — uses the variant's `dot` colour (white for 'blank'). */
function Dot({ variant }: { variant: BadgeVariant }) {
  // The 'blank' (calculation) marker is white — give it a border so it stays
  // visible on the white card surface. Use `.dot` (not `.color`): for 'blank',
  // `.color` is a dark body colour (for the amount text), while `.dot` is white.
  const isBlank = variant === 'blank';
  return (
    <Box
      component="span"
      sx={{
        width: theme.iconSize.xxs,
        height: theme.iconSize.xxs,
        borderRadius: theme.borderRadius.xs,
        bgcolor: BADGE_VARIANTS[variant].dot,
        border: isBlank ? `1px solid ${theme.palette.divider}` : undefined,
        flexShrink: 0,
      }}
    />
  );
}

const monoSx = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;

/**
 * Payslip lines in pay-plan order (`numeroordre`) — a single ordered table, NOT
 * grouped: the category (Gains / employee charges / employer charges / STC /
 * calculation) only drives each row's colour highlight and the legend. No subtotal —
 * amounts are shown as synced, never recomputed. A "show all" toggle reveals the
 * default-hidden (`visible='N'`) rubriques. The public parameter note is a read-only
 * hover tooltip. Ordering/visibility come from the pure {@link orderedRubriques}
 * helper; the per-row category from {@link resolveRubriqueCategory}.
 */
export function PayslipRubriques({ lines }: { lines: PayslipLine[] }) {
  const { t, i18n } = useTranslation('payments');
  const dateFormat = dateFormatForLang(i18n.language);
  const [showAll, setShowAll] = useState(false);

  const rows = useMemo(() => orderedRubriques(lines, showAll), [lines, showAll]);
  const none = t(`${I18N}.none`);

  const formatBase = (line: PayslipLine): string => {
    const { value, isDate } = rubriqueBase(line);
    if (value == null || value === '') return '';
    if (isDate) return formatDate(String(value), dateFormat) || '';
    return formatPayslipNumber(Number(value), i18n.language);
  };

  const legend = (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
      {RUBRIQUE_CATEGORY_ORDER.map(category => (
        <Box key={category} sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
          <Dot variant={RUBRIQUE_CATEGORY_VARIANT[category]} />
          <Typography variant="body2" sx={{ color: theme.palette.text.label }}>
            {t(`${I18N}.categories.${RUBRIQUE_CATEGORY_LABEL_KEY[category]}`)}
          </Typography>
        </Box>
      ))}
      {/* Cross-cutting flag, not a RubriqueCategory — a rubrique can be inactive
          regardless of its category, so it gets its own legend entry. */}
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
        <Dot variant="neutral" />
        <Typography variant="body2" sx={{ color: theme.palette.text.label }}>
          {t(`${I18N}.categories.inactive`)}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <DetailCard
      icon={
        <ReceiptLongOutlinedIcon
          sx={{ fontSize: theme.iconSize.sm, color: theme.palette.primary.main }}
        />
      }
      iconBg={`${theme.palette.primary.main}1F`}
      title={t(`${I18N}.rubriques.title`)}
      // Colour legend sits right after the title; the "show all" toggle stays
      // pushed to the far right for readability.
      titleExtra={legend}
      headerExtra={
        <FormControlLabel
          control={<Switch size="small" checked={showAll} onChange={(_, v) => setShowAll(v)} />}
          label={
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.body,
                fontWeight: theme.typography.fontWeightBold,
              }}
            >
              {t(`${I18N}.rubriques.showAll`)}
            </Typography>
          }
          sx={{ mr: 0, flexShrink: 0 }}
        />
      }
    >
      {rows.length === 0 ? (
        <Typography variant="body2" sx={{ color: theme.palette.text.label }}>
          {t(`${I18N}.rubriques.empty`)}
        </Typography>
      ) : (
        <Table size="small" sx={{ '& td, & th': { borderColor: theme.palette.divider } }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  color: theme.palette.text.label,
                  fontWeight: theme.typography.fontWeightSemiBold,
                  width: 56,
                }}
              >
                {t(`${I18N}.rubriques.columns.order`)}
              </TableCell>
              <TableCell
                sx={{
                  color: theme.palette.text.label,
                  fontWeight: theme.typography.fontWeightSemiBold,
                }}
              >
                {t(`${I18N}.rubriques.columns.code`)}
              </TableCell>
              <TableCell
                sx={{
                  color: theme.palette.text.label,
                  fontWeight: theme.typography.fontWeightSemiBold,
                }}
              >
                {t(`${I18N}.rubriques.columns.label`)}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  color: theme.palette.text.label,
                  fontWeight: theme.typography.fontWeightSemiBold,
                }}
              >
                {t(`${I18N}.rubriques.columns.base`)}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  color: theme.palette.text.label,
                  fontWeight: theme.typography.fontWeightSemiBold,
                }}
              >
                {t(`${I18N}.rubriques.columns.amount`)}
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  color: theme.palette.text.label,
                  fontWeight: theme.typography.fontWeightSemiBold,
                }}
              >
                {t(`${I18N}.rubriques.columns.comment`)}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(line => {
              // Ascending pay-plan order (numeroordre) as sent by the backend — no
              // category grouping; the category is conveyed by the row's colour
              // highlight + the legend. An inactive line (rubriquebulletin.actif_bulletin
              // <> 'O') always renders greyed-out (neutral), overriding its category
              // colour, independently of the "show all" toggle.
              const inactive = isRubriqueInactive(line);
              const cat = inactive
                ? BADGE_VARIANTS.neutral
                : categoryColors(resolveRubriqueCategory(line));
              // Code/label also grey out (not just the amount) so an inactive line
              // reads as greyed everywhere, not only in its category-tinted cell.
              const textColor = inactive ? theme.palette.text.label : theme.palette.text.body;
              return (
                <TableRow
                  key={line.hc_uuid_key}
                  sx={{ bgcolor: cat.bgcolor }}
                  data-inactive={inactive || undefined}
                >
                  <TableCell sx={{ ...monoSx, color: theme.palette.text.label }}>
                    {line.numeroordre ?? ''}
                  </TableCell>
                  <TableCell sx={{ color: textColor }}>{line.code || none}</TableCell>
                  <TableCell sx={{ color: textColor }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      {line.libelle || line.code || none}
                      {line.commentaire && (
                        <Tooltip title={line.commentaire} placement="top" disableInteractive>
                          <InfoOutlinedIcon
                            sx={{
                              fontSize: theme.iconSize.xs,
                              color: theme.palette.text.label,
                              cursor: 'help',
                            }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ ...monoSx, color: theme.palette.text.label }}>
                    {formatBase(line)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      ...monoSx,
                      fontWeight: theme.typography.fontWeightMedium,
                      color: cat.color,
                    }}
                  >
                    {formatPayslipAmount(line.montant, null, none, i18n.language)}
                  </TableCell>
                  <TableCell align="center" sx={{ color: theme.palette.text.label }}>
                    {line.commentaire ? (
                      <Tooltip title={line.commentaire} placement="top" disableInteractive>
                        <InfoOutlinedIcon
                          sx={{
                            fontSize: theme.iconSize.xs,
                            color: theme.palette.text.label,
                            cursor: 'help',
                          }}
                        />
                      </Tooltip>
                    ) : (
                      none
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <Typography
        variant="caption"
        sx={{ display: 'block', mt: 2, color: theme.palette.text.label, lineHeight: 1.5 }}
      >
        {t(`${I18N}.rubriques.hint`)}
      </Typography>
    </DetailCard>
  );
}
